import React from 'react';
import {
  FileTextOutlined,
  FormOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { Button, Drawer, Tooltip } from 'antd';
import { PRETTY_DATE_FORMAT } from '@owl-frontend/components';
import type { UnionExpand } from '@owl-lib/type-util';
import type { Activity } from '../../api/activities/interface';
import type { Citation } from '../../api/claims/interface';
import type { ClaimFile } from '../../api/files/interface';
import { Locale } from '../../context/AppProvider';
import useUserEventHook from '../../data/userEvents/useUserEventHook';
import {
  formatDate,
  formatTitle,
} from '../ClaimOverview/ClaimActivitiesContainer/ActivityCard';
import ActivityCardDeprecated from '../ClaimOverview/ClaimActivitiesContainer/ActivityCardDeprecated';
import {
  ClaimDetailsContext,
  Params,
} from '../ClaimOverview/ClaimDetailsContext';
import ClaimFileDetailsContainer from '../ClaimOverview/ClaimFilesContainer/ClaimFileDetailsContainer';
import ClaimMetadataComponent from '../ClaimOverview/ClaimMetadataComponent/ClaimMetadataComponent';
import useFilesHook from '../ClaimOverview/FilesListContainer/useFilesHook';
import useClaimOverviewHook from '../ClaimOverview/useClaimOverviewHook';
import { UserEventType } from '../interface';
import styles from './Citation.module.scss';

const EMPTY = '-';

const formatPageLabel = (pages: [number, number]): string | number => {
  if (!pages[1] || pages[0] === pages[1]) {
    return pages[0];
  }
  return `${pages[0]} - ${pages[1]}`;
};

const DrawerContent: React.FC<{
  citation: Citation;
  selectedNote: Activity | null;
  selectedFile: ClaimFile | null;
}> = ({ citation, selectedNote, selectedFile }) => {
  if (citation.fragment_type === 'METADATA') {
    return (
      <div>
        <ClaimMetadataComponent />
      </div>
    );
  }
  if (selectedFile) {
    return (
      <ClaimFileDetailsContainer
        file={selectedFile}
        citationPage={citation.pages[0]}
        inDrawer={true}
      />
    );
  }

  if (selectedNote) {
    return (
      <ActivityCardDeprecated
        activity={selectedNote}
        hideBorder
        hideTitle
        removeMoreButton={true}
      />
    );
  }

  return null;
};

const TYPE_ICON: Record<string, { icon: React.ReactNode; color: string }> = {
  DOC: { icon: <FileTextOutlined />, color: 'var(--app-color-info)' },
  file: { icon: <FileTextOutlined />, color: 'var(--app-color-info)' },
  activity: { icon: <FormOutlined />, color: '#7760d7' },
  ACTIVITY: { icon: <FormOutlined />, color: '#7760d7' },
  NOTE: { icon: <FormOutlined />, color: '#7760d7' },
  KNOWLEDGE: { icon: <ReadOutlined />, color: '#d48806' },
};

const CitationTypeIcon: React.FC<{ fragmentType?: string }> = ({
  fragmentType,
}) => {
  const meta = fragmentType ? TYPE_ICON[fragmentType] : undefined;
  if (!meta) {
    return null;
  }
  return (
    <span className={styles.citationIcon} style={{ color: meta.color }}>
      {meta.icon}
    </span>
  );
};

const CitationRender: React.FC<
  React.PropsWithChildren<{
    fragmentType?: string;
    citationIndex: string;
    pages: [number, number];
    hidePage?: boolean;
  }>
> = ({ children, fragmentType, citationIndex, pages, hidePage = false }) => {
  if (children) {
    return <>{children}</>;
  }
  if (fragmentType === 'METADATA') {
    return <>𝑀</>;
  }
  return (
    <>
      <CitationTypeIcon fragmentType={fragmentType} />
      {hidePage
        ? citationIndex
        : `${citationIndex} (${formatPageLabel(pages)})`}
    </>
  );
};

export interface CitationComponentProps extends React.PropsWithChildren {
  citation: Citation;
  citationIndex?: string;
  customCitationHandler?(citation: Citation): void;
  showTooltip?: boolean;
  hidePage?: boolean;
  stateDb?: boolean;
}

export const EscCitation: React.FC<CitationComponentProps> = ({
  citationIndex = 'source',
  citation,
  customCitationHandler,
  children,
  showTooltip = true,
  hidePage = false,
  stateDb,
}) => {
  const {
    activities: { currentActivities },
    claimMetadata,
  } = React.useContext(ClaimDetailsContext);
  const { messages } = React.useContext(Locale);
  const intl = useIntl();
  const [citationDialog, setCitationDialog] = React.useState<boolean>(false);
  const [selectedFile, setSelectedFile] = React.useState<ClaimFile | null>(
    null
  );
  const [selectedNote, setSelectedNote] = React.useState<Activity | null>(null);
  const [isHighlighted, setIsHighlighted] = React.useState<boolean>(false);
  const { dossierId } = useParams() as UnionExpand<Params>;
  const [, userEventHandler] = useUserEventHook();
  const [, claimOverviewHandler] = useClaimOverviewHook(dossierId);
  const [{ files }, handler] = useFilesHook(dossierId);
  const currentFiles = files;
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [isWaitingForFile, setIsWaitingForFile] = React.useState(false);

  const onCitationClick = React.useCallback(() => {
    const { file_id } = citation;
    userEventHandler.addUserEvent({
      dossier_id: dossierId,
      client_dossier_id: claimMetadata?.client_dossier_id || '',
      user_event_type: UserEventType.ClickCitation,
      data: {},
    });
    setCitationDialog(true);
    claimOverviewHandler.setDrawerOpen(true);

    if (citation.fragment_type === 'NOTE') {
      const note = currentActivities.find((a) => a.activity_id === file_id);
      if (!note) {
        return;
      }

      setSelectedNote(note);
      return;
    }
    const file = currentFiles?.[file_id];

    if (!file) {
      setIsWaitingForFile(true);
      handler.fetchFile({ fileId: file_id, stateDb });
    } else {
      setSelectedFile(file);
    }
  }, [
    citation,
    claimMetadata?.client_dossier_id,
    claimOverviewHandler,
    currentActivities,
    currentFiles,
    dossierId,
    handler,
    stateDb,
    userEventHandler,
  ]);

  const getDocumentType = React.useCallback(() => {
    if (citation.fragment_type === 'NOTE') {
      const note = currentActivities.find(
        (a) => a.activity_id === citation.file_id
      );

      if (!note) {
        return EMPTY;
      }

      return note.data
        ? `${formatTitle(note.data.activity_subject)} • ${formatDate(
            note.data.activity_start_date
          )}`
        : `${formatTitle(note.activity_subject)} • ${formatDate(
            note.start_date
          )}`;
    }

    if (citation.fragment_type === 'METADATA') {
      return messages['claim_overview.report.metadata_title'];
    }

    const { file_id } = citation;
    const file = currentFiles?.[file_id];

    if (!file) {
      return EMPTY;
    }

    return `${dayjs(file.received_at).utc().format(PRETTY_DATE_FORMAT)}`;
  }, [citation, currentActivities, currentFiles, messages]);

  React.useEffect(() => {
    if (!isWaitingForFile) {
      return;
    }
    const file = currentFiles?.[citation.file_id];
    if (file) {
      setSelectedFile(file);
      setIsWaitingForFile(false);
    }
  }, [isWaitingForFile, currentFiles, citation.file_id]);

  const renderCitationButton = () => {
    const pageLabel = formatPageLabel(citation.pages);
    const getCitationAriaLabel = () => {
      if (children) {
        return undefined;
      }
      if (citation.fragment_type === 'METADATA') {
        return intl.formatMessage({
          id: 'claim_overview.ask_owl.citation_aria.metadata',
        });
      }
      if (hidePage) {
        return intl.formatMessage(
          { id: 'claim_overview.ask_owl.citation_aria.no_page' },
          { citationIndex }
        );
      }
      return intl.formatMessage(
        { id: 'claim_overview.ask_owl.citation_aria.with_page' },
        { citationIndex, page: pageLabel }
      );
    };

    const citationAriaLabel = getCitationAriaLabel();

    return (
      <Button
        ref={buttonRef}
        aria-label={citationAriaLabel}
        className={clsx(
          children ? styles.citationButtonWithChildren : styles.citationButton,
          isHighlighted && styles.citationButtonHighlighted
        )}
        onClick={() => {
          setIsHighlighted(true);
          if (customCitationHandler) {
            customCitationHandler(citation);
          } else {
            onCitationClick();
          }
        }}
        onBlur={() => {
          setTimeout(() => {
            if (!citationDialog) {
              setIsHighlighted(false);
            }
          }, 0);
        }}
        type={children ? 'link' : undefined}
        size="small"
      >
        <CitationRender
          fragmentType={citation.fragment_type}
          citationIndex={citationIndex}
          pages={citation.pages}
          hidePage={hidePage}
        >
          {children}
        </CitationRender>
      </Button>
    );
  };

  return (
    <>
      {showTooltip ? (
        <Tooltip
          title={
            citation.fragment_type === 'KNOWLEDGE' ? (
              <>
                <strong>{citation.title}</strong>
                {citation.quote ? <div>{citation.quote}</div> : null}
              </>
            ) : (
              'View source'
            )
          }
        >
          {renderCitationButton()}
        </Tooltip>
      ) : (
        renderCitationButton()
      )}
      <Drawer
        title={getDocumentType()}
        placement="right"
        onClose={() => {
          setCitationDialog(false);
          setSelectedNote(null);
          setSelectedFile(null);
          claimOverviewHandler.setDrawerOpen(false);
          setTimeout(() => {
            buttonRef.current?.focus();
          }, 100);
        }}
        open={citationDialog}
        width={citation.fragment_type === 'METADATA' ? '50%' : '85%'}
        className={styles.fileDrawer}
      >
        <DrawerContent
          citation={citation}
          selectedNote={selectedNote}
          selectedFile={selectedFile}
        />
      </Drawer>
    </>
  );
};
